/**
 * LYT Communications - P&L Dashboard
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Admin-only profit & loss dashboard.
 * Revenue (Vexus rates) vs Cost (contractor invoices) vs Margin.
 * Breakdown by project, contractor, work type, time period.
 * Retainage summary in both directions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown, BarChart3, Loader, RefreshCw, Filter } from 'lucide-react';
import { loadFullProject, loadInvoices, fetchFullRates } from '../services/mapService';

function PLDashboard({ darkMode, user, setCurrentPage }) {
  const [segments, setSegments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');

  const bgColor = darkMode ? '#0d1b2a' : '#f8fafc';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const accentPrimary = darkMode ? '#667eea' : '#0077B6';

  useEffect(() => {
    loadData();
  }, []);

  // Client billing rates — fetched from server (key-gated), never in the bundle
  const [fullRateInfo, setFullRateInfo] = useState(null);
  useEffect(() => {
    let live = true;
    fetchFullRates('vexus-la-tx-2026')
      .then(d => { if (live && d) setFullRateInfo(d); })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, []);
  const vexusRates = fullRateInfo?.rates || {};

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await loadFullProject(null);
      setSegments(data?.segments || []);
      const invs = await loadInvoices(null);
      setInvoices(invs);
    } catch (err) {
      console.error('[PLDashboard] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const projects = useMemo(() => [...new Set(segments.map(s => s.project_id).filter(Boolean))], [segments]);

  const filteredSegments = useMemo(() => {
    if (filterProject === 'all') return segments;
    return segments.filter(s => s.project_id === filterProject);
  }, [segments, filterProject]);

  const filteredInvoices = useMemo(() => {
    if (filterProject === 'all') return invoices;
    return invoices.filter(i => i.project_id === filterProject);
  }, [invoices, filterProject]);

  // Calculate revenue (what Vexus pays us) from QC-approved segments
  const revenue = useMemo(() => {
    let total = 0;
    const byType = { boring: 0, pulling: 0, splicing: 0, structures: 0 };

    filteredSegments.forEach(seg => {
      const footage = parseFloat(seg.footage) || 0;
      const actualFootage = parseFloat(seg.boring_actual_footage) || footage;

      if (seg.boring_status === 'QC Approved') {
        const rate = vexusRates?.UG1?.vexus || 0;
        const amt = actualFootage * rate;
        total += amt;
        byType.boring += amt;
      }
      if (seg.pulling_status === 'QC Approved') {
        const ct = parseInt(seg.pulling_cable_type) || 24;
        const code = ct > 144 ? 'UG28' : 'UG4';
        const rate = vexusRates?.[code]?.vexus || 0;
        const amt = footage * rate;
        total += amt;
        byType.pulling += amt;
      }
    });

    return { total, byType };
  }, [filteredSegments, vexusRates]);

  // Calculate costs from contractor invoices
  const costs = useMemo(() => {
    let total = 0;
    let retainageHeld = 0;
    let retainageReleased = 0;
    let paid = 0;
    let pending = 0;
    const byContractor = {};

    filteredInvoices.forEach(inv => {
      const sub = parseFloat(inv.subtotal) || 0;
      const ret = parseFloat(inv.retainage_amount) || 0;
      total += sub;

      if (inv.retainage_status === 'Held') retainageHeld += ret;
      else retainageReleased += ret;

      if (inv.status === 'Paid') paid += parseFloat(inv.paid_amount) || 0;
      if (inv.status === 'Submitted' || inv.status === 'Approved') pending += parseFloat(inv.net_amount) || 0;

      const contractor = inv.contractor_company || 'Unknown';
      if (!byContractor[contractor]) byContractor[contractor] = 0;
      byContractor[contractor] += sub;
    });

    return { total, retainageHeld, retainageReleased, paid, pending, byContractor };
  }, [filteredInvoices]);

  const margin = revenue.total - costs.total;
  const marginPct = revenue.total > 0 ? (margin / revenue.total * 100) : 0;

  const formatCurrency = (amt) => `$${(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} color={accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => setCurrentPage('admin-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textColor, padding: 4 }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: textColor, margin: 0 }}>Profit & Loss</h1>
          <p style={{ color: mutedColor, fontSize: '0.85rem', margin: 0 }}>Revenue vs Cost vs Margin</p>
        </div>
        <select
          value={filterProject}
          onChange={e => setFilterProject(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${borderColor}`, background: cardBg, color: textColor, fontSize: 13 }}
        >
          <option value="all">All Projects</option>
          {projects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button onClick={loadData} style={{ padding: 8, background: 'none', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer', color: textColor }}>
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Top-line cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Revenue (Vexus)', value: formatCurrency(revenue.total), icon: TrendingUp, color: '#4CAF50' },
          { label: 'Cost (Contractors)', value: formatCurrency(costs.total), icon: TrendingDown, color: '#FF9800' },
          { label: 'Gross Margin', value: formatCurrency(margin), icon: DollarSign, color: margin >= 0 ? '#4CAF50' : '#FF4444', subtitle: `${marginPct.toFixed(1)}%` },
          { label: 'Retainage Held', value: formatCurrency(costs.retainageHeld), icon: BarChart3, color: '#FFB800' },
        ].map((stat, idx) => (
          <div key={idx} style={{ background: cardBg, borderRadius: 12, padding: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${stat.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <stat.icon size={20} color={stat.color} />
              </div>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '0.85rem', color: mutedColor }}>{stat.label}</div>
            {stat.subtitle && <div style={{ fontSize: '0.8rem', fontWeight: 600, color: stat.color }}>{stat.subtitle}</div>}
          </div>
        ))}
      </div>

      {/* Revenue Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: cardBg, borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: textColor, fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Revenue by Type</h3>
          {Object.entries(revenue.byType).filter(([, v]) => v > 0).map(([type, amount]) => (
            <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${borderColor}` }}>
              <span style={{ color: textColor, textTransform: 'capitalize' }}>{type}</span>
              <span style={{ fontWeight: 600, color: '#4CAF50' }}>{formatCurrency(amount)}</span>
            </div>
          ))}
          {Object.values(revenue.byType).every(v => v === 0) && (
            <p style={{ color: mutedColor, textAlign: 'center', padding: 16 }}>No QC-approved revenue yet.</p>
          )}
        </div>

        <div style={{ background: cardBg, borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: textColor, fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Cost by Contractor</h3>
          {Object.entries(costs.byContractor).sort((a, b) => b[1] - a[1]).map(([name, amount]) => (
            <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${borderColor}` }}>
              <span style={{ color: textColor }}>{name}</span>
              <span style={{ fontWeight: 600, color: '#FF9800' }}>{formatCurrency(amount)}</span>
            </div>
          ))}
          {Object.keys(costs.byContractor).length === 0 && (
            <p style={{ color: mutedColor, textAlign: 'center', padding: 16 }}>No contractor invoices yet.</p>
          )}
        </div>
      </div>

      {/* Payment Status */}
      <div style={{ background: cardBg, borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <h3 style={{ color: textColor, fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Payment Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          {[
            { label: 'Total Paid', value: formatCurrency(costs.paid), color: '#4CAF50' },
            { label: 'Pending Approval', value: formatCurrency(costs.pending), color: '#FFB800' },
            { label: 'Retainage Held', value: formatCurrency(costs.retainageHeld), color: '#FF9800' },
            { label: 'Retainage Released', value: formatCurrency(costs.retainageReleased), color: '#2196F3' },
          ].map((stat, idx) => (
            <div key={idx} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '0.8rem', color: mutedColor }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Segment Progress Summary */}
      <div style={{ background: cardBg, borderRadius: 12, padding: 20 }}>
        <h3 style={{ color: textColor, fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Work Progress</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          {[
            { label: 'Total Segments', value: filteredSegments.length, color: textColor },
            { label: 'Boring QC Approved', value: filteredSegments.filter(s => s.boring_status === 'QC Approved').length, color: '#4CAF50' },
            { label: 'Pulling QC Approved', value: filteredSegments.filter(s => s.pulling_status === 'QC Approved').length, color: '#2196F3' },
            { label: 'Total Footage', value: `${filteredSegments.reduce((s, seg) => s + (parseFloat(seg.footage) || 0), 0).toLocaleString()} LF`, color: accentPrimary },
          ].map((stat, idx) => (
            <div key={idx} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '0.8rem', color: mutedColor }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PLDashboard;
